import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import apiConfig from '../config/apiConfig';

function normalizeVerificationToken(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';

  const metaContent = value.match(/content\s*=\s*["']([^"']+)["']/i);
  if (metaContent?.[1]) return metaContent[1].trim();

  const eqForm = value.match(/google-site-verification\s*=\s*([^\s"'<>]+)/i);
  if (eqForm?.[1]) return eqForm[1].trim();

  if (!/[<>]/.test(value)) return value;

  return value.replace(/<[^>]*>/g, '').trim();
}

const SiteSeoContext = createContext({
  twitterHandle: '',
  googleSiteVerification: '',
  bingSiteVerification: '',
  hreflang: { enabled: true, xDefaultLanguage: 'en', includeRegionalVariants: true },
  loaded: false,
});

export function SiteSeoProvider({ children }) {
  const [settings, setSettings] = useState({
    twitterHandle: '',
    googleSiteVerification: '',
    bingSiteVerification: '',
    hreflang: { enabled: true, xDefaultLanguage: 'en', includeRegionalVariants: true },
    loaded: false,
  });

  useEffect(() => {
    axios
      .get(`${apiConfig.BASE_URL}/seo/public`)
      .then((res) => {
        const data = res.data?.data || {};
        setSettings({
          twitterHandle: data.twitterHandle || '',
          googleSiteVerification: normalizeVerificationToken(data.googleSiteVerification),
          bingSiteVerification: normalizeVerificationToken(data.bingSiteVerification),
          hreflang: data.hreflang || { enabled: true, xDefaultLanguage: 'en' },
          loaded: true,
        });
      })
      .catch(() => {
        setSettings((prev) => ({ ...prev, loaded: true }));
      });
  }, []);

  return <SiteSeoContext.Provider value={settings}>{children}</SiteSeoContext.Provider>;
}

export function useSiteSeo() {
  return useContext(SiteSeoContext);
}

export function useSeoExtras() {
  const seo = useSiteSeo();
  const twitterSite = seo.twitterHandle
    ? seo.twitterHandle.startsWith('@')
      ? seo.twitterHandle
      : `@${seo.twitterHandle}`
    : undefined;

  return {
    twitterSite,
    googleSiteVerification: seo.googleSiteVerification || undefined,
    bingSiteVerification: seo.bingSiteVerification || undefined,
    hreflangEnabled: seo.hreflang?.enabled !== false,
    xDefaultLanguage: seo.hreflang?.xDefaultLanguage || 'en',
    includeRegionalVariants: seo.hreflang?.includeRegionalVariants !== false,
  };
}
